import {
  FilterOptions,
  InvestigationRow,
  MapareRow,
  RunDetail,
  SummaryStats,
} from '@lyra/shared';
import { TABLE_BUCKET_LABELS } from './labels';
import { noMatchKind } from './wizard';

/**
 * Ported verbatim from v1 `app/src/utils/index.ts` — same thresholds/predicates,
 * only the type name changed (`ClassifiedRow` → `MapareRow`, an alias of the same
 * shape in `@lyra/shared`).
 */
export function rowNeedsManualReview(row: MapareRow): boolean {
  if (row.status === 'error') return false;
  return (
    row.workflowStatus === 'ambiguous' ||
    row.workflowStatus === 'needs_review' ||
    row.workflowStatus === 'no_match' ||
    // Parity fix (v1 had a dedicated „Încredere scăzută" review step): a
    // low-confidence classification must surface in „De verificat", not fall
    // silently into the collapsed „Rezolvate" group.
    row.workflowStatus === 'low_confidence'
  ) && row.reviewResolution == null;
}

export function rowHasFinalClassification(row: MapareRow): boolean {
  if (row.status === 'error') return false;
  if (row.reviewResolution === 'unresolved') return false;
  return Boolean(row.codMedoc && row.denumireMedoc);
}

/**
 * The wizard's "Generează separat" (ambiguous split) stamps the ORIGINAL row
 * `reviewResolution:'expanded'` with a blank MEDOC code — a superseded audit
 * row; its child rows (separate `MapareRow`s, `reviewResolution:null`) carry
 * the real classifications. Such a row must never be bucketed into any of
 * the four `groupResults` sections (it would otherwise fall through
 * `groupOf`'s precedence into `rezolvate`, inflating the resolved count with
 * a blank-code row) nor appear in any export. The wizard's own `wizardBucket`
 * (`lib/wizard.ts`) already handles `'expanded'` correctly (→ `'done'`,
 * hidden) — this predicate is the equivalent exclusion for the results-table
 * side, kept in its own function (rather than folded into `groupOf`) so both
 * `groupResults` and `buildExport` (`lib/export.ts`) can exclude it up front,
 * the same way both already exclude pending rows.
 */
export function isSupersededByExpansion(row: MapareRow): boolean {
  return row.reviewResolution === 'expanded';
}

/**
 * Calculates summary statistics from classified rows. Ported verbatim from v1.
 */
export function calculateStats(rows: MapareRow[]): SummaryStats {
  return {
    total: rows.length,
    correct: rows.filter((r) => rowHasFinalClassification(r) && r.accepted !== false).length,
    needsReview: rows.filter((r) => rowNeedsManualReview(r)).length,
    edited: rows.filter((r) => r.status === 'edited' || r.reviewResolution === 'selected_alternate').length,
    errors: rows.filter((r) => r.status === 'error').length,
    unresolved: rows.filter((r) => r.reviewResolution === 'unresolved').length,
  };
}

/**
 * Filters and sorts rows based on filter options. Search/confidence-band
 * filtering and the sort comparators are ported verbatim from v1. The status
 * filter's four `MaparePage`-tab branches (`correct`/`review_pending`/
 * `unresolved`/`error`) are NOT v1-verbatim — Fix 2+3 routes them through the
 * shared `groupOf` helper (defined below) instead, so a tab's contents
 * always match its corresponding `groupResults` section; see `groupOf`'s doc
 * comment for why the two used to disagree.
 */
export function filterAndSortRows(
  rows: MapareRow[],
  filters: FilterOptions
): MapareRow[] {
  // Fix 1 (DEAD SORT): copy up front. When search/confidence/status are all
  // at defaults, none of the branches below reassign `filtered` to a new
  // array — without this copy, `filtered` stays === `rows` and the
  // `.sort()` below mutates the caller's array (the `buildRowsFromRun`
  // memo output) IN PLACE, returning the same reference. That breaks
  // `ResultsTable`'s `grouped = useMemo(..., [rows])` identity check on the
  // „Toate" view (sort controls silently do nothing) and reorders the
  // shared array out from under the export path. Any later per-filter
  // `.filter()` call already produces a new array, so this is only load-
  // bearing on the default/no-filter path — harmless (redundant) otherwise.
  let filtered = [...rows];

  // Search filter
  if (filters.searchTerm) {
    const term = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(
      (r) =>
        r.code.toLowerCase().includes(term) ||
        r.name.toLowerCase().includes(term)
    );
  }

  // Confidence filter
  if (filters.confidenceRange !== 'all') {
    filtered = filtered.filter((r) => {
      if (filters.confidenceRange === 'high') return r.confidence >= 0.9;
      if (filters.confidenceRange === 'medium') return r.confidence >= 0.7 && r.confidence < 0.9;
      if (filters.confidenceRange === 'low') return r.confidence < 0.7;
      return true;
    });
  }

  // Status filter. Fix 2+3 (tab ≠ group): the four `MaparePage` STATUS_TABS
  // values ('correct'/'review_pending'/'unresolved'/'error') now route
  // through the SAME `groupOf` precedence `groupResults` uses to bucket the
  // table's sections, instead of separate ad-hoc predicates that could (and
  // did) disagree with it — see `groupOf`'s doc comment. Pending rows are
  // excluded from every group-backed tab, matching `groupResults`'s own
  // pending exclusion (a pending row has no final result yet, so it can't
  // belong to any of the four sections). The remaining legacy
  // `FilterOptions['status']` values (finer-grained `workflowStatus`/`status`
  // matches) aren't reachable from any current UI control but are left
  // intact for type completeness and any direct caller relying on them.
  if (filters.status !== 'all') {
    const group = STATUS_TAB_TO_GROUP[filters.status];
    if (group) {
      filtered = filtered.filter(
        (r) => r.status !== 'pending' && !isSupersededByExpansion(r) && groupOf(r) === group
      );
    } else if (
      filters.status === 'resolved' ||
      filters.status === 'ambiguous' ||
      filters.status === 'needs_review' ||
      filters.status === 'no_match' ||
      filters.status === 'low_confidence'
    ) {
      filtered = filtered.filter((r) => r.workflowStatus === filters.status);
    } else {
      filtered = filtered.filter((r) => r.status === filters.status);
    }
  }

  // Sorting
  filtered.sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    switch (filters.sortBy) {
      case 'confidence':
        aVal = a.confidence;
        bVal = b.confidence;
        break;
      case 'name':
        aVal = a.name;
        bVal = b.name;
        break;
      case 'code':
        aVal = a.code;
        bVal = b.code;
        break;
      case 'codMedoc':
        aVal = a.codMedoc || a.provisionalCodMedoc || '';
        bVal = b.codMedoc || b.provisionalCodMedoc || '';
        break;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return filters.sortOrder === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return filters.sortOrder === 'asc'
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number);
  });

  return filtered;
}

/**
 * Ported verbatim from v1 `useClassification.ts`'s `createPendingRow` (~L88) —
 * builds a synthetic "not classified yet" row for an input row that has no
 * matching entry, using the negative-id convention (`-(index + 1)`) so pending
 * rows never collide with real (positive) DB entry ids.
 */
function createPendingRow(
  sourceRow: InvestigationRow,
  index: number,
  run: Pick<RunDetail, 'id' | 'filename'>
): MapareRow {
  return {
    ...sourceRow,
    id: -(index + 1),
    rowIndex: index,
    runId: run.id,
    runFilename: run.filename,
    codMedoc: '',
    denumireMedoc: '',
    confidence: 0,
    reasoning: '',
    status: 'pending',
  };
}

/**
 * Ported verbatim from v1 `useClassification.ts`'s `buildRowsFromRun` (~L100-129).
 * Consumes the shared `RunDetail` (camelCase wire contract from GET /runs/:id).
 * Preserves: merging `sourceData`/`value` from the run's `inputRows` into each
 * entry (input rows are the fidelity source for those two fields), dedup by
 * `rowIndex` (entries keyed into a Map, last write per index wins — matches v1),
 * and filling any gap between entries with a pending row (negative-id convention)
 * so the returned array always has one row per input row up to `totalRows`.
 */
export function buildRowsFromRun(run: RunDetail): MapareRow[] {
  const byRowIndex = new Map<number, MapareRow>();
  run.entries.forEach((entry, entryIndex) => {
    const index = entry.rowIndex ?? entryIndex;
    byRowIndex.set(index, {
      ...entry,
      rowIndex: index,
      runId: run.id,
      runFilename: run.filename,
      sourceData: run.inputRows[index]?.sourceData ?? entry.sourceData,
      value: run.inputRows[index]?.value ?? entry.value,
    });
  });

  const rows: MapareRow[] = [];
  const totalRows = Math.max(run.totalRows, run.inputRows.length, byRowIndex.size);
  for (let index = 0; index < totalRows; index += 1) {
    const existing = byRowIndex.get(index);
    if (existing) {
      rows.push(existing);
      continue;
    }

    const sourceRow = run.inputRows[index];
    if (sourceRow) {
      rows.push(createPendingRow(sourceRow, index, run));
    }
  }
  return rows;
}

export interface GroupedResults {
  /** Unresolved ambiguous/needs_review/no_match — needs an operator decision. */
  deVerificat: MapareRow[];
  /** Resolved, but the resolution is "no match in MEDOC" (not an error). */
  faraCorespondent: MapareRow[];
  /** Everything else — rows carrying a final MEDOC classification. */
  rezolvate: MapareRow[];
  /** Classification errored out for this row. */
  erori: MapareRow[];
}

/**
 * Fix 2+3 (no_match row stuck in Fără corespondent / tab ≠ group): the ONE
 * canonical bucket a *non-pending* row belongs to. Both `groupResults` (the
 * table's section buckets) and `filterAndSortRows`'s status-tab filtering
 * (the „Toate"/„Rezolvate"/„De verificat"/„Fără corespondent"/„Erori" tabs)
 * now call this instead of each carrying its own, previously-diverging
 * predicate — that divergence was the bug: a corrected no_match row (picked
 * from the nomenclator → `reviewResolution:'selected_alternate'` + a final
 * code, but `workflowStatus` still `'no_match'`) stayed bucketed under
 * „Fără corespondent" even though it now has a final classification, and a
 * bulk-`no_match_confirmed` row (server sets `reviewResolution:
 * 'confirmed_primary'`) was in the faraCorespondent GROUP but missed by the
 * old „Fără corespondent" TAB (which only matched
 * `reviewResolution === 'unresolved'`).
 *
 * Precedence (mutually exclusive — every non-pending row lands in exactly
 * one bucket):
 *   1. erori           — status === 'error'
 *   2. deVerificat      — rowNeedsManualReview(row) (already excludes errors)
 *   3. faraCorespondent — resolved as "no match" AND not already carrying a
 *                         final code: reviewResolution === 'unresolved', OR
 *                         (a GENUINE no_match — `noMatchKind(row) === 'no_match'`,
 *                         so NOT a similarity-threshold low_confidence — AND NOT
 *                         rowHasFinalClassification(row))
 *                         — that last clause is Fix 2: a no_match row that
 *                         now HAS a final code (e.g. corrected via the
 *                         nomenclator search) is no longer „fără
 *                         corespondent"; it falls through to rezolvate.
 *   4. rezolvate        — everything remaining (final classification)
 *
 * Pending rows (status === 'pending') are NOT covered by this precedence —
 * they have no final result yet (the live progress bar represents them
 * instead), so callers exclude them before/around calling `groupOf` (see
 * `groupResults` below and `filterAndSortRows`'s status-tab branch).
 */
export function groupOf(row: MapareRow): keyof GroupedResults {
  if (row.status === 'error') return 'erori';
  // „Fără cod final" = no final classification exists. A `no_match` row (the
  // pipeline found no code) belongs here from the START — reviewed or not —
  // because it has neither a code nor candidates to choose among; that is
  // exactly what the „Fără cod final" label was renamed to mean. So does any
  // row a human explicitly marked „unresolved". (Previously an unreviewed
  // no_match row went to „De verificat" via `rowNeedsManualReview`, merging two
  // distinct kinds in that bucket and leaving „Fără cod final" empty until a
  // manual step — the reported #2 bug.) Only a no_match row a human later gave
  // a real code (`rowHasFinalClassification`) leaves here for „Rezolvate".
  if (row.reviewResolution === 'unresolved') return 'faraCorespondent';
  // Only a GENUINE no_match is terminal „Fără cod final". A `no_match` row
  // whose ambiguityReason is a similarity-threshold miss is `low_confidence`
  // per `noMatchKind` — the wizard routes it to the ACTIONABLE „Încredere
  // scăzută" step, so the table must keep it actionable too (it falls through
  // to „De verificat" via `rowNeedsManualReview`), not strand it as terminal.
  // (I1: `groupOf` and `wizardBucket` must not disagree on that row — one
  // reading terminal while the other reads actionable is the toggle showing
  // two contradictory faces of the same row.)
  if (noMatchKind(row) === 'no_match' && !rowHasFinalClassification(row)) return 'faraCorespondent';
  // Rows still holding candidates or an uncertain-but-present code (ambiguous /
  // needs_review / low_confidence, unreviewed) need a human decision.
  if (rowNeedsManualReview(row)) return 'deVerificat';
  return 'rezolvate';
}

/** Maps the four group-backed `MaparePage` STATUS_TABS values to the
 *  `groupOf` bucket they represent — `filterAndSortRows`'s status filter
 *  (Fix 2+3). `'all'` and every other legacy `FilterOptions['status']` value
 *  are intentionally absent (handled separately, see call site). */
const STATUS_TAB_TO_GROUP: Partial<Record<FilterOptions['status'], keyof GroupedResults>> = {
  correct: 'rezolvate',
  review_pending: 'deVerificat',
  unresolved: 'faraCorespondent',
  error: 'erori',
};

/**
 * New (not a port): buckets rows for the Direction-B status-grouped display.
 * Delegates each non-pending row to `groupOf` (Fix 2+3) — pending rows are
 * excluded entirely up front, so the four buckets partition only the
 * *final* (non-pending) rows of the input; they don't have a final
 * classification yet, so they don't belong under any of the four
 * (final-result) section headers, least of all „Rezolvate".
 */
export function groupResults(rows: MapareRow[]): GroupedResults {
  const deVerificat: MapareRow[] = [];
  const faraCorespondent: MapareRow[] = [];
  const rezolvate: MapareRow[] = [];
  const erori: MapareRow[] = [];

  const buckets: Record<keyof GroupedResults, MapareRow[]> = {
    deVerificat,
    faraCorespondent,
    rezolvate,
    erori,
  };

  for (const row of rows) {
    if (row.status === 'pending') continue;
    // Superseded wizard-split parent (see `isSupersededByExpansion`): its
    // child rows carry the real classification, so this row belongs in NONE
    // of the four buckets — excluded up front exactly like pending rows,
    // rather than left for `groupOf` to fall through into `rezolvate`.
    if (isSupersededByExpansion(row)) continue;
    buckets[groupOf(row)].push(row);
  }

  return { deVerificat, faraCorespondent, rezolvate, erori };
}

/** One item in the flat, virtualizable render list for Task 10's `ResultsTable`. */
export type ResultsRenderItem =
  | {
      type: 'header';
      key: string;
      group: keyof GroupedResults;
      label: string;
      count: number;
      hint?: string;
      collapsible?: boolean;
      /** Task 5 / #2: this section's rows carry a leading selection checkbox — render
       *  the "Selectează tot" affordance next to the label. `deVerificat` and
       *  `faraCorespondent` set this (both are bulk-actionable review buckets). */
      selectable?: boolean;
    }
  | { type: 'collapsed-toggle'; key: string; label: string }
  /** `group` (Task 5) tags which `GroupedResults` bucket a row/detail item came from,
   *  so `ResultsTable` can branch its leading-cell (checkbox) and detail-row (correction
   *  panel vs. plain reasoning) rendering without re-deriving group membership per row. */
  | { type: 'row'; key: string; row: MapareRow; group: keyof GroupedResults }
  | { type: 'detail'; key: string; row: MapareRow; group: keyof GroupedResults };

/**
 * New (not a port): flattens `GroupedResults` into a single ordered list of
 * render-items — section headers interleaved with row items (and, right
 * after a row, an optional reasoning-detail item) — so `ResultsTable` can
 * drive ONE `useVirtualizer` over it instead of nesting a virtualizer per
 * section (which would defeat windowing whenever more than one section is
 * long). Section order is fixed: De verificat, Fără corespondent, Rezolvate,
 * Erori. Empty sections are omitted entirely. `Rezolvate` collapses to a
 * single "Afișează celelalte {n} ↓" toggle item when `rezolvateExpanded` is
 * false; once expanded it renders like any other section (with a "Ascunde ↑"
 * affordance left to the caller to attach to the `header` item whose
 * `collapsible` flag is set). `expandedRowId` controls which single row (if
 * any) gets its reasoning `detail` item inserted directly after it.
 */
export function buildRenderItems(
  grouped: GroupedResults,
  opts: { rezolvateExpanded: boolean; expandedRowId: number | null; rezolvateCollapsible?: boolean }
): ResultsRenderItem[] {
  // `rezolvateCollapsible` false (#3): the section is shown expanded but WITHOUT
  // the „Ascunde ↑" affordance — used when the operator has filtered *to*
  // „Rezolvate", where collapsing would just re-hide the rows they asked for.
  const { rezolvateExpanded, expandedRowId, rezolvateCollapsible = true } = opts;
  const items: ResultsRenderItem[] = [];

  function pushSection(
    key: keyof GroupedResults,
    label: string,
    rows: MapareRow[],
    extra?: { hint?: string; collapsible?: boolean; selectable?: boolean }
  ) {
    if (rows.length === 0) return;
    items.push({ type: 'header', key: `header-${key}`, group: key, label, count: rows.length, ...extra });
    for (const row of rows) {
      items.push({ type: 'row', key: `row-${row.id}`, row, group: key });
      if (expandedRowId === row.id) {
        items.push({ type: 'detail', key: `detail-${row.id}`, row, group: key });
      }
    }
  }

  pushSection('deVerificat', 'De verificat', grouped.deVerificat, {
    hint: 'navigare cu tastatura (Tab)',
    selectable: true,
  });
  pushSection('faraCorespondent', TABLE_BUCKET_LABELS.faraCorespondent, grouped.faraCorespondent, {
    selectable: true,
  });

  if (grouped.rezolvate.length > 0) {
    if (rezolvateExpanded) {
      pushSection('rezolvate', 'Rezolvate', grouped.rezolvate, { collapsible: rezolvateCollapsible });
    } else {
      items.push({
        type: 'collapsed-toggle',
        key: 'rezolvate-toggle',
        label: `Afișează cele ${grouped.rezolvate.length} rânduri rezolvate`,
      });
    }
  }

  pushSection('erori', 'Erori', grouped.erori);

  return items;
}

/**
 * Ported verbatim from v1 `app/src/utils/index.ts`'s `groupRunsByDate` —
 * same bucketing rule (Azi / Ieri / formatted-date label, „Azi"/„Ieri" pinned
 * first, the rest ordered by most-recent-first) and the same defensive
 * `r.created_at ? new Date(r.created_at) : new Date()` fallback.
 *
 * Only the type signature changed: v1 took a bespoke `RunForGroup` shape;
 * this takes any `{ created_at: string }`-shaped row (generic, so it accepts
 * the v2 `RunSummary` — snake_case `created_at` — directly without a
 * duplicate type).
 */
export function groupRunsByDate<T extends { created_at: string }>(
  runs: T[]
): Array<{ label: string; runs: T[] }> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups = new Map<string, T[]>();
  for (const r of runs) {
    const d = r.created_at ? new Date(r.created_at) : new Date();
    d.setHours(0, 0, 0, 0);
    let label: string;
    if (d.getTime() === today.getTime()) label = 'Azi';
    else if (d.getTime() === yesterday.getTime()) label = 'Ieri';
    else label = d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(r);
  }
  const order = ['Azi', 'Ieri'];
  const result: Array<{ label: string; runs: T[] }> = [];
  for (const label of order) {
    if (groups.has(label)) result.push({ label, runs: groups.get(label)! });
  }
  const rest = [...groups.entries()]
    .filter(([l]) => !order.includes(l))
    .sort(([, a], [, b]) => {
      const ta = a[0]?.created_at ? new Date(a[0].created_at).getTime() : 0;
      const tb = b[0]?.created_at ? new Date(b[0].created_at).getTime() : 0;
      return tb - ta;
    });
  for (const [label, runs] of rest) result.push({ label, runs });
  return result;
}
